import React, { useCallback, useState, useEffect } from 'react';
import { usePathname } from 'expo-router';
import { Link } from '@/lib/navigation';
import { StyleSheet, View, Platform, Pressable, ScrollView, KeyboardAvoidingView, Modal, StatusBar } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { Stack } from 'expo-router';
import PropTypes from 'prop-types';
import { APP_NAME } from '@/constants/config';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import DesktopAuthShell from '@/components/Auth/DesktopAuthShell';
import Heading from '@/components/Typography/Heading';
import Logo from '@/components/Icons/Logo';
import { useTheme } from '@/context/ThemeContext';
import { WebView } from 'react-native-webview';
import Icon from '@/components/Icon';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  leftSection: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollView: {
    width: '100%',
    minHeight: '100%',
  },
  contentContainer: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  formContainer: {
    width: '100%',
    maxWidth: 400,
    gap: 6,
  },
  footer: {
    gap: 12,
    marginTop: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    alignItems: 'center',
    marginBottom: 32,
  },
  logo: {
    width: 48,
    height: 48,
    marginBottom: 24,
  },
  title: {
    marginBottom: 8,
    marginTop: 16,
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
    maxWidth: 320,
  },
  keyboardAvoidingView: {
    flex: 1,
    width: '100%',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
  },
  closeButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 20,
  },
  webview: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  mobileLink: {
    minHeight: 44,
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  legalLinks: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 16,
  },
  footerText: {
    textAlign: 'center',
    lineHeight: 25,
    verticalAlign: 'middle',
  }
});

function AuthPage({
  title,
  subtitle,
  onLogoPress,
  children,
  showHeader = true,
  headerShown = false,
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const showLogo = Platform.OS === 'web';
  const pathname = usePathname();
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const isMobile = Platform.OS === 'ios' || Platform.OS === 'android';
  const insets = useSafeAreaInsets();

  // Show Terms & Privacy on login and signup (pathname can vary on native, e.g. /login or /auth/login)
  const showLegalLinks = pathname?.includes('signup') || pathname?.includes('login');

  // Set status bar color on Android to match theme
  useEffect(() => {
    if (Platform.OS === 'android') {
      StatusBar.setBarStyle(colors.status_bar_style, true);
      StatusBar.setBackgroundColor(colors.solid_color_primary, true);
    }
  }, [colors]);

  const handleTermsPress = () => {
    if (isMobile) {
      setShowTerms(true);
    }
  };

  const handlePrivacyPress = () => {
    if (isMobile) {
      setShowPrivacy(true);
    }
  };

  const renderHeader = useCallback(() => (
    <View style={styles.header}>
      {showLogo && (
        <Pressable onPress={onLogoPress}>
          <Logo 
            width={styles.logo.width}
            height={styles.logo.height}
            color={colors.text_color_primary}
            style={styles.logo}
            resizeMode="contain"
            accessibilityLabel={APP_NAME}
          />
        </Pressable>
      )}
      <Heading h1={!isMobile} h2={isMobile} style={styles.title}>{title}</Heading>
      {subtitle && <Text style={styles.subtitle} color='secondary'>{subtitle}</Text>}
    </View>
  ), [onLogoPress, showLogo, title, subtitle, colors]);

  if (Platform.OS === 'web') return <>
    <Stack.Screen options={{ title: `${title} | ${APP_NAME}`, headerShown }} />
    <DesktopAuthShell title={showHeader ? title : undefined} subtitle={showHeader ? subtitle : undefined} onLogoPress={onLogoPress} showLegal={showLegalLinks}>
      {children}
    </DesktopAuthShell>
  </>;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: `${title} | ${APP_NAME}`,
          headerShown,
        }}
      />

      <View style={styles.leftSection}>
        {isMobile ? (
          <KeyboardAwareScrollView
            style={styles.scrollView}
            contentContainerStyle={[styles.contentContainer, { paddingBottom: Math.max(insets.bottom, 20) + 20 }]}
            showsVerticalScrollIndicator={false}
            bottomOffset={Platform.OS === 'android' ? 40 : 20}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.formContainer}>
              {showHeader && renderHeader()}

              {children}

              {showLegalLinks && (
                <View style={styles.footer}>
                  <Text size="s" color="tertiary" style={styles.footerText}>
                    By continuing, you agree to our
                  </Text>
                  <View style={styles.legalLinks}>
                    <Pressable accessibilityRole="link" onPress={handleTermsPress} style={styles.mobileLink}>
                      <Text size="s" color="primary">{t('common.termsOfService')}</Text>
                    </Pressable>
                    <Pressable accessibilityRole="link" onPress={handlePrivacyPress} style={styles.mobileLink}>
                      <Text size="s" color="primary">{t('common.privacyPolicy')}</Text>
                    </Pressable>
                  </View>
                </View>
              )}
            </View>
          </KeyboardAwareScrollView>
        ) : (
          <KeyboardAvoidingView 
            style={styles.keyboardAvoidingView}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            keyboardVerticalOffset={Platform.OS === 'ios' ? 64 : 0}
          >
            <ScrollView
              style={styles.scrollView}
              contentContainerStyle={styles.contentContainer}
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.formContainer}>
                {showHeader && renderHeader()}

                {children}

                {showLegalLinks && (
                  <View style={styles.footer}>
                    <Text size="m" color="secondary" style={styles.footerText}>
                      By continuing you agree to our{' '}
                      <Link href="/terms-of-service">
                        <Text size="m" color="primary" style={styles.mobileLink} onPress={handleTermsPress}>
                          {t('common.termsOfService')}
                        </Text>
                      </Link>
                      {' and '}
                      <Link href="/privacy-policy">
                        <Text size="m" color="primary" style={styles.mobileLink} onPress={handlePrivacyPress}>
                          {t('common.privacyPolicy')} 
                        </Text>
                      </Link>
                    </Text>
                  </View>
                )}
              </View>
            </ScrollView>
          </KeyboardAvoidingView>
        )}
      </View>

      {/* Terms of Service WebView Modal */}
      <Modal
        visible={showTerms}
        animationType="slide"
        onRequestClose={() => setShowTerms(false)}
      >
        <View style={[styles.modalContainer, { backgroundColor: colors.solid_color_primary }]}>
          <View style={[
            styles.modalHeader, 
            { 
              borderBottomColor: colors.border_color_secondary,
              paddingTop: insets.top,
            }
          ]}>
            <Text weight="bold" size="l">Gab AI Terms of Service</Text>
            <Pressable accessibilityRole="button" accessibilityLabel="Close terms of service" onPress={() => setShowTerms(false)} style={styles.closeButton}>
              <Icon name="xmark" size={20} color={colors.text_color_primary} />
            </Pressable>
          </View>
          <WebView
            style={styles.webview}
            source={{ uri: 'https://gab.ai/terms-of-service' }}
          />
        </View>
      </Modal>

      {/* Privacy Policy WebView Modal */}
      <Modal
        visible={showPrivacy}
        animationType="slide"
        onRequestClose={() => setShowPrivacy(false)}
      >
        <View style={[styles.modalContainer, { backgroundColor: colors.solid_color_primary }]}>
          <View style={[
            styles.modalHeader, 
            { 
              borderBottomColor: colors.border_color_secondary,
              paddingTop: insets.top,
            }
          ]}>
            <Text weight="bold" size="l">Gab AI Privacy Policy</Text>
            <Pressable accessibilityRole="button" accessibilityLabel="Close privacy policy" onPress={() => setShowPrivacy(false)} style={styles.closeButton}>
              <Icon name="xmark" size={20} color={colors.text_color_primary} />
            </Pressable>
          </View>
          <WebView
            style={styles.webview}
            source={{ uri: 'https://gab.ai/privacy-policy' }}
          />
        </View>
      </Modal>
    </View>
  );
}

AuthPage.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  onLogoPress: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  showHeader: PropTypes.bool,
  headerShown: PropTypes.bool,
};

export default React.memo(AuthPage);
